/**
 * Filter daftar transaksi yang disimpan di query string, sehingga tautan dari
 * dashboard dan hasil ekspor tetap terbaca setelah halaman dimuat ulang atau
 * dibagikan ke operator lain.
 */

import { PAYMENT_STATUSES, type PaymentQuery, type PaymentStatus } from './apiTypes'

export type PaymentFilters = {
  status: PaymentStatus[]
  merchant: string
  client: string
  from: string
  to: string
  clientReference: string
  reference: string
  needsReview: boolean
}

export const emptyPaymentFilters: PaymentFilters = {
  status: [],
  merchant: '',
  client: '',
  from: '',
  to: '',
  clientReference: '',
  reference: '',
  needsReview: false,
}

const datePattern = /^\d{4}-\d{2}-\d{2}$/

function isPaymentStatus(value: string): value is PaymentStatus {
  return (PAYMENT_STATUSES as string[]).includes(value)
}

/** Membaca filter dari query string; nilai yang tidak dikenal diabaikan. */
export function parsePaymentFilters(params: URLSearchParams): PaymentFilters {
  const status = (params.get('status') ?? '')
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(isPaymentStatus)

  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''

  return {
    status,
    merchant: params.get('merchant') ?? '',
    client: params.get('client') ?? '',
    from: datePattern.test(from) ? from : '',
    to: datePattern.test(to) ? to : '',
    clientReference: params.get('client_reference') ?? '',
    reference: params.get('reference') ?? '',
    needsReview: params.get('needs_review') === 'true',
  }
}

/** Menulis filter ke query string. Nilai kosong tidak ditulis. */
export function serializePaymentFilters(filters: PaymentFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.status.length > 0) {
    params.set('status', filters.status.join(','))
  }
  if (filters.merchant) {
    params.set('merchant', filters.merchant)
  }
  if (filters.client) {
    params.set('client', filters.client)
  }
  if (datePattern.test(filters.from)) {
    params.set('from', filters.from)
  }
  if (datePattern.test(filters.to)) {
    params.set('to', filters.to)
  }
  if (filters.clientReference) {
    params.set('client_reference', filters.clientReference)
  }
  if (filters.reference) {
    params.set('reference', filters.reference)
  }
  if (filters.needsReview) {
    params.set('needs_review', 'true')
  }
  return params
}

/** Jumlah filter aktif, dipakai tombol bersihkan dan keadaan terfilter habis. */
export function countActivePaymentFilters(filters: PaymentFilters): number {
  let count = 0
  if (filters.status.length > 0) count += 1
  if (filters.merchant) count += 1
  if (filters.client) count += 1
  if (filters.from) count += 1
  if (filters.to) count += 1
  if (filters.clientReference) count += 1
  if (filters.reference) count += 1
  if (filters.needsReview) count += 1
  return count
}

/** Menambahkan atau membuang satu status dari daftar filter. */
export function togglePaymentStatus(
  filters: PaymentFilters,
  status: PaymentStatus,
): PaymentFilters {
  const statuses = filters.status.includes(status)
    ? filters.status.filter((item) => item !== status)
    : [...filters.status, status].sort(
        (a, b) => PAYMENT_STATUSES.indexOf(a) - PAYMENT_STATUSES.indexOf(b),
      )
  return { ...filters, status: statuses }
}

/** Parameter yang dikirim ke GET /payments, tanpa cursor. */
export function paymentQueryFromFilters(filters: PaymentFilters): Omit<PaymentQuery, 'cursor'> {
  return {
    status: filters.status.length > 0 ? filters.status : undefined,
    merchant: filters.merchant || undefined,
    client: filters.client || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    client_reference: filters.clientReference || undefined,
    reference: filters.reference || undefined,
    needs_review: filters.needsReview || undefined,
  }
}
